import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { User } from 'oidc-client-ts';
import { userManager } from '../contexts/AuthProvider';
import { callExchangeTokenEndpoint } from '../services/tokenService';
import { appConfig } from '../config/appConfig';

interface CallbackHandlerProps {
  children: React.ReactNode;
}

export const CallbackHandler: React.FC<CallbackHandlerProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isProcessingToken, setIsProcessingToken] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  // Track retry attempt number so spinner message updates ("Retrying...")
  const [retryCount, setRetryCount] = useState(0);
  const hasProcessedRef = useRef(false);

  // Check if current URL has callback and we haven't started processing yet
  const isCallback = location.search.includes('code=') && location.search.includes('state=');

  // Function to process the callback
  const processCallback = useCallback(async () => {
        if (hasProcessedRef.current) return;
            hasProcessedRef.current = true;
        try {
          // Extract code from URL
          const urlParams = new URLSearchParams(location.search);
          const code = urlParams.get('code');
          const sessionStateFromUrl = urlParams.get('session_state'); // Capture session_state from callback URL
          if (!code) {
            throw new Error('No authorization code received from OIDC provider');
          }
                    const codeVerifier = getCodeVerifierFromStorage();
          setIsProcessingToken(true);

          // onRetry is called by withRetry before each retry attempt.
          // Instead of showing an error, the spinner message updates — the user
          // never sees an error screen during transient Azure AD slowness.
          const onRetry = (attempt: number, maxAttempts: number) => {
            setRetryCount(attempt);
            console.log(`[CallbackHandler] Azure AD slow — spinner shown, attempt ${attempt}/${maxAttempts}`);
          };

          const exchangeResponse = await callExchangeTokenEndpoint(code, codeVerifier, onRetry);

          // Store tokens in OIDC UserManager for persistent authentication
          if (exchangeResponse.access_token && exchangeResponse.id_token) {
            
            const profile = decodeJwt(exchangeResponse.id_token);
            if (!profile || !profile.exp) {
              console.error('Invalid token structure');
              return false;
            }
            const now = Math.floor(Date.now() / 1000);
            const expires_at = exchangeResponse.expires_in ? now + exchangeResponse.expires_in : undefined;
            const user = new User({
              access_token: exchangeResponse.access_token,
              id_token: exchangeResponse.id_token,
              refresh_token: exchangeResponse.refresh_token,
              token_type: 'Bearer',
              scope: 'openid profile email', // or your actual scope
              profile,
              expires_at,
              session_state: sessionStateFromUrl || null, // Use session_state from URL callback
            });
            
            // Reuse shared userManager (prevents extra signinRedirect state entries)
            await userManager.storeUser(user);
            // Manually trigger UserLoaded event to update AuthProvider state immediately
            await userManager.events.load(user, true);
          }
          navigate(appConfig.APP_HOME_URL || '/', { replace: true });

        } catch (error: any) {
          setTokenError(error.message || 'Failed to exchange authorization code');
        } finally {
          setIsProcessingToken(false);
        }
      }, [location.search, navigate]);

  useEffect(() => {
    if (isCallback && !hasProcessedRef.current && !isProcessingToken && !tokenError) {
      processCallback();
    }
  }, [isCallback, isProcessingToken, tokenError, processCallback]);
  // JWT decoding helper
  const decodeJwt = (token: string) => {
    try {
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    } catch (error) {
      console.error('Failed to decode JWT:', error);
      return null;
    }
  };

  // Function to get the latest code_verifier from localStorage
  const getCodeVerifierFromStorage = () => {
    let codeVerifier = null;
    let latestCreated = 0;

    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.includes("oidc")) {
        try {
          const item = window.localStorage.getItem(key);
          if (item) {
            const obj = JSON.parse(item);
            if (
              obj?.code_verifier &&
              obj?.created &&
              obj.created > latestCreated
            ) {
              codeVerifier = obj.code_verifier;
              latestCreated = obj.created;
            }
          }
        } catch (e) {
          // Ignore parsing errors for invalid JSON
        }
      }
    }

    return codeVerifier;
  };

  // If we're in callback mode, show processing screen
  if (isCallback) {
    if (tokenError) {
      // Only reached here after ALL retries (3) are exhausted — Azure AD is truly unreachable.
      // Detect if it was a timeout/overload error so we can show a retry-friendly message
      const isTimeoutError =
        tokenError.toLowerCase().includes('timeout') ||
        tokenError.toLowerCase().includes('503') ||
        tokenError.toLowerCase().includes('unavailable');

      return (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>Authentication Error</h2>
          <p>{tokenError}</p>
          {isTimeoutError && (
            <p style={{ color: '#555', fontSize: '14px', marginBottom: '16px' }}>
              The authentication service is still busy after multiple attempts. Please try again shortly.
            </p>
          )}
          {/* Retry button — clears processed flag so processCallback can run again */}
          {isTimeoutError && (
            <button
              style={{ marginRight: '12px' }}
              onClick={() => {
                hasProcessedRef.current = false;
                setRetryCount(0);
                setTokenError(null);
              }}
            >
              Retry
            </button>
          )}
          <button onClick={() => navigate(appConfig.APP_HOME_URL || '/', { replace: true })}>
            Return to Home
          </button>
        </div>
      );
    }

    // Build spinner status message.
    // retryCount > 0 means Azure AD is slow — we keep showing spinner, not error.
    const spinnerMessage =
      retryCount === 0
        ? 'Completing authentication, please wait...'
        : `Authentication service is busy, please wait\u2026 (attempt ${retryCount + 1} of 4)`;

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '5px solid #f3f3f3',
          borderTop: '5px solid #0557D5',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px'
        }} />
        <p style={{ color: '#444', fontSize: '15px', fontFamily: 'sans-serif', textAlign: 'center', maxWidth: '320px' }}>
          {spinnerMessage}
        </p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Normal app rendering
  return <>{children}</>;
};
